import { addApolloState, initializeApollo } from '@/utils/apolloClient'
import type { NextPage, GetServerSidePropsContext } from 'next'
import React from 'react'
import ReactMarkdown from 'react-markdown'
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Box,
  Button,
  Center,
  Link,
  Heading,
  Text,
  Flex,
  useColorMode,
  useColorModeValue,
} from '@chakra-ui/react'
import { useTranslations } from 'next-intl'

import { SITE_URL } from '@/constants'
import { customerReviews } from 'data/customerReviews'
import { getMineBitcoinPageData } from '@/api/strapi/mineBitcoinPage'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { CustomerReviews } from '@/components/CustomerReviews'
import { Hero } from '@/features/minebitcoin/Hero'
import { EarningsCalculator } from '@/features/minebitcoin/EarningsCalculator'
import { MiningVsBuying } from '@/features/minebitcoin/MiningVsBuying'
import { Metrics } from '@/components/Metrics'
import { VerticalSlideshow } from '@/components/VerticalSlideshow'
import SeoHead from '@/components/seo-head'
import { MostRecentCompassContent } from '@/features/home/MostRecentCompassContent'
import { IFAQsCard } from '@/types/strapi/commonComponents.interface'
import { IMineBitcoinPage } from '@/types/strapi/mineBitcoinPage.interface'

type Props = {
  pageData: IMineBitcoinPage
}

const MineBitcoinPage: NextPage<Props> = ({ pageData }) => {
  const t = useTranslations()

  const secondaryBg = useColorModeValue(
    'lightModeSecondaryBG',
    'darkModeSecondaryBG'
  )
  const theme = useColorMode()

  // some FAQ items should be opened by default. Get these items to pass to Accordion
  const openedFaqItems = pageData?.FAQs?.FAQs?.length
    ? pageData.FAQs.FAQs.reduce((acc: number[], item: IFAQsCard, index) => {
        if (item.isOpened) {
          acc.push(index)
        }
        return acc
      }, [])
    : [0]

  return (
    <>
      <SeoHead
        title={t('minebitcoin.seo.title')}
        breadcrumbTitle={t('minebitcoin.seo.breadcrumb')}
        description={t('minebitcoin.seo.description')}
        image={`${SITE_URL}/img/og-minebitcoin.jpg`}
      />
      <Hero />
      {pageData?.statistics?.published && (
        <Box bg={secondaryBg} id="results">
          <ContainerBase
            maxWidth={{ xxl: 'container.xl' }}
            py={{ base: 8, lg: 20 }}
          >
            <Metrics
              title={pageData.statistics.title}
              subtitle={pageData.statistics.subtitle}
              statistics={pageData.statistics.cards}
            >
              {pageData.statistics.link && (
                <Button
                  as={Link}
                  variant="outlineGradient"
                  href={pageData.statistics.link.url}
                  isExternal={pageData.statistics.link.newTab}
                  w="full"
                  fontSize="18px"
                  minHeight="48px"
                >
                  <Text>{pageData.statistics.link.text}</Text>
                </Button>
              )}
            </Metrics>
          </ContainerBase>
        </Box>
      )}
      <Box id="comparison">
        <ContainerBase
          maxWidth={{ xxl: 'container.xl' }}
          py={{ base: 8, lg: '60px' }}
        >
          <MiningVsBuying />
        </ContainerBase>
      </Box>
      {pageData?.getStarted?.published && (
        <Box id="why-us">
          <ContainerBase
            maxWidth={{ xxl: 'container.xl' }}
            py={{ base: 8, lg: '60px' }}
          >
            <VerticalSlideshow
              title={pageData.getStarted.title}
              subtitle={pageData.getStarted.subtitle}
              slideshow={pageData.getStarted.slideshow}
            />
          </ContainerBase>
        </Box>
      )}
      <Box position="relative" bg={secondaryBg}>
        {theme.colorMode === 'dark' && (
          <Flex
            width="100%"
            height="100%"
            overflow="hidden"
            zIndex={1}
            position="absolute"
            top="0%"
            left="0%"
          >
            <Box
              position="absolute"
              top="50%"
              left="50%"
              w="964px"
              h="964px"
              rounded="100%"
              background="radial-gradient(65.37% 65.37% at 52.49% 48.96%, #0060FF 0%, #00FFBD 100%);"
              filter="blur(200px)"
              transform={{
                base: 'translate(-50%, -50%)',
                md: 'translate(-50%, -50%)',
              }}
            ></Box>
          </Flex>
        )}
        <ContainerBase
          zIndex={2}
          position="relative"
          maxWidth={{ xxl: 'container.xl' }}
          py={{ base: 8, lg: '60px' }}
        >
          <EarningsCalculator />
        </ContainerBase>
      </Box>
      <Center id="reviews" py={{ base: 8, lg: 10 }}>
        <CustomerReviews
          title={t('minebitcoin.reviews.title')}
          subtitle={t('minebitcoin.reviews.subtitle')}
          reviews={customerReviews}
        />
      </Center>
      {pageData?.FAQs?.published && (
        <Box id="faq">
          <ContainerBase
            maxWidth={{ xxl: 'container.xl' }}
            py={{ base: 8, lg: 10 }}
          >
            <Heading
              variant="spaceGrotesk"
              fontWeight={300}
              textAlign="center"
              fontSize="32px"
              mb={{ base: 8, lg: 10 }}
            >
              {pageData.FAQs.title}
            </Heading>
            <Accordion allowMultiple defaultIndex={openedFaqItems}>
              {pageData.FAQs?.FAQs.map((item: IFAQsCard) => (
                <AccordionItem
                  key={item.id}
                  border="1px solid"
                  borderColor="gray.200"
                  mb={4}
                >
                  <Box>
                    <AccordionButton>
                      <Flex width="100%" justifyContent="space-between">
                        <Text
                          fontSize="20px"
                          fontWeight={700}
                          lineHeight="normal"
                        >
                          {item.title}
                        </Text>
                        <AccordionIcon color="brand.600" fontSize="26px" />
                      </Flex>
                    </AccordionButton>
                  </Box>
                  <AccordionPanel>
                    <Box fontSize="20px">
                      <ReactMarkdown className="markdown">
                        {item.description}
                      </ReactMarkdown>
                    </Box>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </ContainerBase>
        </Box>
      )}
      <MostRecentCompassContent />
      {pageData?.disclaimer && (
        <ContainerBase py={4}>
          <Center fontSize="sm" fontWeight={400} opacity={0.6}>
            <Box>
              <ReactMarkdown className="markdown">
                {pageData.disclaimer}
              </ReactMarkdown>
            </Box>
          </Center>
        </ContainerBase>
      )}
    </>
  )
}

export async function getServerSideProps({
  locale,
}: GetServerSidePropsContext) {
  const apolloClient = initializeApollo()
  const { data } = await apolloClient.query({
    query: getMineBitcoinPageData,
    variables: {
      locale,
    },
    context: {
      clientName: 'strapi',
    },
  })

  if (!data) {
    return addApolloState(apolloClient, {
      notFound: true,
    })
  }

  const pageData = data?.mineBitcoinPage || {}

  return {
    props: {
      messages: (await import(`../../locales/${locale}.json`)).default,
      pageData,
    },
  }
}

export default MineBitcoinPage
